import { Button, Modal, ModalContext, toast } from "dash-ui";
import { useContext, useMemo, useRef, useState } from "react";
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function CropImageModal() {
  const { isOpenCurrent, toggleModal, payload } = useContext(ModalContext);

  const originalFile = payload?.file as File;

  const imgSrc = useMemo(() => {
    if (!originalFile) return null;
    return URL.createObjectURL(originalFile);
  }, [payload]);

  // Remember: crop's `width` and `height` will be saved in percentage, so we can center it on image load.
  const [crop, setCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement | null>(null); // Reference to the image element

  const isOpen = isOpenCurrent["CropImageModal"];

  // Helper function to ensure crop is centered when image loaded
  function handleImageLoad(e) {
    const { naturalWidth: width, naturalHeight: height } = e.currentTarget;

    const crop = centerCrop(
      makeAspectCrop(
        {
          // You don't need to pass a complete crop into
          // makeAspectCrop or centerCrop.
          unit: "%",
          width: 80,
        },
        4 / 3,
        width,
        height
      ),
      width,
      height
    );

    setCrop(crop);
  }

  const handleCropAndUpload = () => {
    if (!crop || !imgRef.current) return;

    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Convert crop percentages to pixels
    const width = (crop.width / 100) * image.width;
    const height = (crop.height / 100) * image.height;
    const x = (crop.x / 100) * image.width;
    const y = (crop.y / 100) * image.height;

    canvas.width = width;
    canvas.height = height;

    if (!ctx) {
      console.error("Failed to get canvas context");
      return;
    }

    // Draw image in canvas to convert to file later.
    ctx.drawImage(
      image,
      x * scaleX,
      y * scaleY,
      width * scaleX,
      height * scaleY,
      0,
      0,
      width,
      height
    );

    // Convert drawn image to file/blob and upload
    canvas.toBlob(
      async (blob) => {
        if (!blob) return;

        const extension = originalFile?.type?.split("/")[1] || "png";
        const croppedFile = new File([blob], `cropped-image.${extension}`, {
          type: originalFile?.type,
        });

        try {
          // Here we call avatar upload mutation
          payload?.onComplete(croppedFile);
          toggleModal(false, "CropImageModal");
        } catch (error) {
          toast.error(`Error uploading file: ${error}`);
        }
      },
      originalFile?.type,
      1
    );
  };

  if (!originalFile) return null;

  return (
    <Modal
      shouldCloseOnOverlayClick={false}
      title="Crop image"
      w={"fit-content"}
      maxWidth={"85vw"}
      maxHeight={"85vh"}
      onRequestClose={() => toggleModal(false, "CropImageModal")}
      isOpen={isOpen}>
      <div className="flex px-4 mt-4 flex-col gap-4 items-center justify-center">
        {imgSrc && (
          <ReactCrop
            className="max-h-[65vh]"
            crop={crop}
            onChange={(crop, percentCrop) => setCrop(percentCrop)}
            aspect={4 / 3}>
            <img src={imgSrc} ref={imgRef} onLoad={handleImageLoad} />
          </ReactCrop>
        )}
        <div className="flex flex-row gap-4">
          <Button w={90} onClick={handleCropAndUpload}>
            Save
          </Button>
          <Button
            w={90}
            onClick={() => toggleModal(false, "CropImageModal")}
            variation="outline">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default CropImageModal;
